import React, { useState, useEffect } from 'react';
import { DndContext, closestCenter, DragEndEvent, DragOverlay } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Dialog, DialogContent, DialogClose } from '@/components/atoms/Dialog';
import MenuIcon from '@/assets/icons/Market/eva_menu-outline.svg?react';
import { Button } from '@/components/atoms/PcButton';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

// referenceMarketArray의 각 요소로 고유 키 생성
const getMarketKey = (market) =>
  `${market.MarketNameEnglish}_${market.MarketId}_${market.StoreId || ''}`;

const SortableMarketItem = ({ refMarket, activeId, isDraggingOverlay }) => {
  const marketKey = getMarketKey(refMarket);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } =
    useSortable({ id: marketKey });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: 'transform 0.25s cubic-bezier(0.22, 1, 0.36, 1)', // 항상 transition 적용
    opacity: isDragging ? 0.7 : 1,
    background: isDragging ? '#F3F3F9' : isOver ? '#eaf2ff' : undefined,
    borderRadius: 30,
    boxShadow: isDragging ? '0 4px 16px 0 #4e7eff33' : isOver ? '0 2px 8px 0 #4e7eff22' : undefined,
    zIndex: isDragging ? 100 : undefined,
    position: 'relative',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex select-none items-center justify-between bg-white p-[10px] transition-all
        ${isDragging ? 'shadow-xl' : ''}`}
      {...attributes}
      {...listeners}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <img
          src={refMarket.MarketIconUrl}
          alt={`${refMarket.MarketAlias || ''} logo`}
          className="h-7 w-7 rounded-full"
        />
        <span className="truncate text-gr-800 Body2M16">
          {refMarket?.StoreName || refMarket?.StoreId}
        </span>
      </div>
      <span className="flex items-center">
        <MenuIcon />
      </span>
    </div>
  );
};

const ChangeSequence = ({
  setTempSequence,
  referenceMarketArray,
  handleUpdateSequence,
  open,
  onOpenChange,
}) => {
  // referenceMarketArray 순서대로 key 배열 생성
  const [items, setItems] = useState(referenceMarketArray.map(getMarketKey));
  const [activeId, setActiveId] = useState(null);

  // 다이얼로그 열릴 때마다 초기화
  useEffect(() => {
    if (open) {
      const initialIds = referenceMarketArray.map(getMarketKey);
      setItems(initialIds);
      setTempSequence(referenceMarketArray); // tempSequence는 referenceMarketArray만!
    }
    // eslint-disable-next-line
  }, [open, referenceMarketArray]);

  // items가 바뀔 때마다 tempSequence도 동기화 (referenceMarketArray 순서에 맞게)
  useEffect(() => {
    const newTempSeq = items
      .map((key) => referenceMarketArray.find((m) => getMarketKey(m) === key))
      .filter(Boolean);
    setTempSequence(newTempSeq);
    // eslint-disable-next-line
  }, [items, referenceMarketArray]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (over && active.id !== over.id) {
      const oldIndex = items.indexOf(active.id);
      const newIndex = items.indexOf(over.id);
      setItems((prev) => arrayMove(prev, oldIndex, newIndex));
      // tempSequence 동기화는 useEffect에서 자동으로 처리됨
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent noCloseBtn className="max-w-[400px] px-[20px] py-[30px]">
        <div className="flex flex-col gap-[20px]">
          <div className="text-gr-900 Title3S18">표시되는 스토어 순서를 변경해 보세요</div>
          <div className="scrollbar max-h-[318px] overflow-y-auto">
            <DndContext
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={handleDragEnd}
              onDragStart={(e) => setActiveId(e.active.id)}
            >
              <SortableContext items={items} strategy={verticalListSortingStrategy}>
                {items.map((key) => {
                  const refMarket = referenceMarketArray.find((m) => getMarketKey(m) === key);
                  // registeredMarketList는 화면 표시용으로만 필요하면 사용
                  return (
                    <SortableMarketItem
                      key={key}
                      refMarket={refMarket}
                      registeredMarket={null}
                      activeId={activeId}
                      isDraggingOverlay={false}
                    />
                  );
                })}
              </SortableContext>
            </DndContext>
          </div>
          <div className="flex  gap-3">
            <DialogClose asChild>
              <Button variant="muted" className="h-[44px] flex-1">
                취소
              </Button>
            </DialogClose>
            <Button
              className="h-[44px] flex-1"
              onClick={() => {
                handleUpdateSequence(); // tempSequence는 이미 최신 상태
                onOpenChange(false);
              }}
            >
              확인
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChangeSequence;
